import moment from 'moment-timezone';
import { CADENCE_DAY_OF_WEEK, CADENCE_IDS } from './constants/recurrenceApi.constants';

export const RECURRENCE_MAX_INSTANCES = 12;

export const RECURRENCE_CADENCE = {
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
};

const getNextStartDateForCadence = (currentStartDate, cadence) => {
    switch (cadence) {
        case RECURRENCE_CADENCE.DAILY:
            return currentStartDate.clone().add(1, 'day');
        case RECURRENCE_CADENCE.WEEKLY:
            return currentStartDate.clone().add(1, 'week');
        case RECURRENCE_CADENCE.MONTHLY: {
            const weekday = currentStartDate.day();
            const weekOfMonth = Math.ceil(currentStartDate.date() / 7);

            const targetMonth = currentStartDate.clone().add(1, 'month');
            const next = targetMonth.clone().startOf('month');

            // find first occurrence of weekday in target month
            while (next.day() !== weekday) {
                next.add(1, 'day');
            }

            // jump to nth occurrence
            next.add((weekOfMonth - 1) * 7, 'days');

            // fallback if overflow (e.g. 5th weekday doesn't exist)
            if (next.month() !== targetMonth.month()) {
                next.subtract(7, 'days');
            }

            // Preserve original time (DST-safe)
            next.hour(currentStartDate.hour());
            next.minute(currentStartDate.minute());
            next.second(0);
            next.millisecond(0);

            return next;
        }
        case RECURRENCE_CADENCE.YEARLY:
            return currentStartDate.clone().add(1, 'year');
        default:
            return currentStartDate.clone().add(1, 'week');
    }
};

const addOverlapErrors = (instances, errors) => {
    const activeInstances = instances.filter(instance => !instance.excluded);
    for (let i = 0; i < activeInstances.length; i += 1) {
        for (let j = i + 1; j < activeInstances.length; j += 1) {
            const first = activeInstances[i];
            const second = activeInstances[j];
            const firstStart = moment.utc(first.eventStartUtc);
            const firstEnd = moment.utc(first.eventEndUtc);
            const secondStart = moment.utc(second.eventStartUtc);
            const secondEnd = moment.utc(second.eventEndUtc);
            const doesOverlap = firstStart.isBefore(secondEnd) && secondStart.isBefore(firstEnd);
            if (doesOverlap) {
                errors.push(`Event ${activeInstances[i].order} overlaps Event ${activeInstances[j].order}.`);
            }
        }
    }
};

export const isSingleDayEvent = ({ eventStartUtc, eventEndUtc, timezoneValue }) => {
    if (!eventStartUtc || !eventEndUtc || !timezoneValue) {
        return false;
    }
    const start = moment.utc(eventStartUtc).tz(timezoneValue);
    const end = moment.utc(eventEndUtc).tz(timezoneValue);
    return start.isSame(end, 'day');
};

export const getCadenceOptions = ({ includeDaily }) => {
    const options = [
        { id: RECURRENCE_CADENCE.DAILY, label: 'Daily', disabled: !includeDaily },
        { id: RECURRENCE_CADENCE.WEEKLY, label: 'Weekly' },
        { id: RECURRENCE_CADENCE.MONTHLY, label: 'Monthly' },
        { id: RECURRENCE_CADENCE.YEARLY, label: 'Yearly' },
    ];
    return includeDaily ? options : options.filter(option => option.id !== RECURRENCE_CADENCE.DAILY);
};

export function mapFrontendCadenceToId(cadence) {
    let cadenceId = null;
    switch (cadence) {
        case RECURRENCE_CADENCE.DAILY:
            cadenceId = CADENCE_IDS.DAILY;
            break;
        case RECURRENCE_CADENCE.WEEKLY:
            cadenceId = CADENCE_IDS.WEEKLY;
            break;
        case RECURRENCE_CADENCE.MONTHLY:
            cadenceId = CADENCE_IDS.MONTHLY;
            break;
        case RECURRENCE_CADENCE.YEARLY:
            cadenceId = CADENCE_IDS.YEARLY;
            break;
        default:
            break;
    }
    return cadenceId;
}

export function mapCadenceIdToFrontendCadence(cadenceId) {
    switch (cadenceId) {
        case CADENCE_IDS.DAILY:
            return RECURRENCE_CADENCE.DAILY;
        case CADENCE_IDS.WEEKLY:
            return RECURRENCE_CADENCE.WEEKLY;
        case CADENCE_IDS.MONTHLY:
            return RECURRENCE_CADENCE.MONTHLY;
        case CADENCE_IDS.YEARLY:
            return RECURRENCE_CADENCE.YEARLY;
        default:
            return null;
    }
}

export function mapMomentDayToBackendDay(momentDate) {
    const jsDay = momentDate.day();
    return jsDay === 0 ? CADENCE_DAY_OF_WEEK.SUNDAY : jsDay;
}

export const generateRecurringScheduleInstances = ({
    name,
    schedule,
    recurrenceSettings,
    timezoneValue,
    maxInstances = RECURRENCE_MAX_INSTANCES,
    matchRegistrationStartForAll = true,
}) => {
    const fallback = {
        instances: [],
        computedOccurrences: null,
        computedEndDateUtc: null,
    };
    if (!schedule || !recurrenceSettings || !timezoneValue || !schedule.eventStartUtc || !schedule.eventEndUtc || !schedule.registrationEndUtc) {
        return fallback;
    }

    const {
        cadenceId,
        numberOfOccurrences,
        endsOnDate,
    } = recurrenceSettings;

    const startBase = moment.utc(schedule.eventStartUtc).tz(timezoneValue);
    const endBase = moment.utc(schedule.eventEndUtc).tz(timezoneValue);
    const regStartBase = schedule.registrationStartUtc
        ? moment.utc(schedule.registrationStartUtc).tz(timezoneValue)
        : null;
    const regEndBase = moment.utc(schedule.registrationEndUtc).tz(timezoneValue);

    if (!startBase.isValid() || !endBase.isValid() || !regEndBase.isValid()) {
        return fallback;
    }

    const eventDurationMinutes = Math.round(endBase.diff(startBase, 'minutes', true));
    const regEndOffsetMinutes = Math.round(regEndBase.diff(endBase, 'minutes', true));
    const regStartOffsetMinutes = regStartBase
        ? Math.round(regStartBase.diff(startBase, 'minutes', true))
        : null;

    const recurrenceCadence = mapCadenceIdToFrontendCadence(cadenceId);
    if (!recurrenceCadence) {
        return fallback;
    }

    const includeDaily = isSingleDayEvent({ eventStartUtc: schedule.eventStartUtc, eventEndUtc: schedule.eventEndUtc, timezoneValue });
    if (recurrenceCadence === RECURRENCE_CADENCE.DAILY && !includeDaily) {
        // no validation here anymore
    }

    let maxCount = 0;
    if (numberOfOccurrences) {
        maxCount = parseInt(numberOfOccurrences, 10) || 0;
    } else if (endsOnDate) {
        const endBoundary = moment.utc(endsOnDate).tz(timezoneValue).endOf('day');
        let cursor = startBase.clone();
        let safety = 0;
        while (cursor.valueOf() <= endBoundary.valueOf() && safety < maxInstances) {
            maxCount += 1;
            safety += 1;
            cursor = getNextStartDateForCadence(cursor, recurrenceCadence);
        }
    }

    if (maxCount > maxInstances) {
        maxCount = maxInstances;
    }

    const instances = [];
    let cursor = startBase.clone();
    for (let index = 0; index < maxCount; index += 1) {
        const eventStart = cursor.clone().second(0).millisecond(0);
        const eventEnd = eventStart.clone().add(eventDurationMinutes, 'minutes');
        const registrationEnd = eventEnd.clone().add(regEndOffsetMinutes, 'minutes');
        let registrationStart = null;

        if (matchRegistrationStartForAll) {
            registrationStart = regStartBase ? regStartBase.clone() : null;
        } else {
            registrationStart = regStartOffsetMinutes !== null
                ? eventStart.clone().add(regStartOffsetMinutes, 'minutes')
                : null;
        }

        const instance = {
            id: 0,
            index,
            name,
            order: index + 1,
            isNameOverridden: false,
            eventStartUtc: eventStart.toISOString(),
            eventEndUtc: eventEnd.toISOString(),
            registrationStartUtc: registrationStart ? registrationStart.toISOString() : null,
            registrationEndUtc: registrationEnd.toISOString(),
        };
        instances.push(instance);
        cursor = getNextStartDateForCadence(cursor, recurrenceCadence)
            .hour(startBase.hour())
            .minute(startBase.minute())
            .second(0)
            .millisecond(0);
    }

    const last = instances[instances.length - 1];

    return {
        instances,
        computedOccurrences: instances.length || null,
        computedEndDateUtc: last ? last.eventStartUtc : null,
    };
};

export const validateRecurringScheduleInstances = (instances, maxInstances = RECURRENCE_MAX_INSTANCES) => {
    const errors = [];

    if (!instances?.length) {
        errors.push('At least one recurring event instance is required.');
        return { errors };
    }

    if (instances.length > maxInstances) {
        errors.push(`Recurring events are limited to ${maxInstances} instances.`);
    }

    addOverlapErrors(instances, errors);

    return { errors };
};
